/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  cancelBlackCard,
  myBlackCardSubscription,
  subscribeBlackCard,
  updateBlackCardPaymentMethod,
} from '../../apollo/server';
import { Header } from '../../components/Header';
import CreditCardOffRoundedIcon from '@mui/icons-material/CreditCardOffRounded';
import RmfDialog, { DialogActions as RmfDialogActions, DialogIcon, DialogText, DialogTitle as RmfDialogTitle, dialogButtonSx } from '../../components/Dialog/RmfDialog';
import ConfigurableValues from '../../config/constants';
import UserContext from '../../context/User';
import ConfigurationContext from '../../context/Configuration';
import QorPaymentForm from '../../components/Checkout/QorPaymentForm';
import useBlackCardProgram, { bcMoney } from '../../hooks/useBlackCardProgram';

// Icons
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BoltIcon from '@mui/icons-material/Bolt';
import StarIcon from '@mui/icons-material/Star';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { FONT_BODY } from '../../utils/designTokens';

const MY_BLACK_CARD = gql`${myBlackCardSubscription}`;
const SUBSCRIBE_BLACK_CARD = gql`${subscribeBlackCard}`;
const CANCEL_BLACK_CARD = gql`${cancelBlackCard}`;
const UPDATE_PAYMENT_METHOD = gql`${updateBlackCardPaymentMethod}`;

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

// The perks follow the super admin's Black Card settings; one set to 0 is off
// and its cards drop out.
function benefitsFor({ deliveryDiscountCap: cap, bonusPointsPerOrder: points }) {
  return [
    cap > 0 && {
      icon: LocalShippingIcon, accent: '#fbbf24',
      title: `Up to ${bcMoney(cap)} off delivery`,
      text: `Platform covers up to ${bcMoney(cap)} of the delivery fee on every order`,
    },
    cap > 0 && {
      icon: BoltIcon, accent: '#f59e0b',
      title: 'Transparent overage',
      text: `If the delivery fee exceeds ${bcMoney(cap)}, you pay only the difference (e.g. ${bcMoney(cap + 2)} fee → you pay $2)`,
    },
    points > 0 && {
      icon: StarIcon, accent: '#fde047',
      title: `+${points} loyalty points per order`,
      text: 'Bonus points stacked on top of your regular tier earnings',
    },
    {
      icon: EventBusyIcon, accent: '#94a3b8',
      title: 'Cancel anytime',
      text: 'Benefits keep working until your current period ends',
    },
  ].filter(Boolean);
}

// ── Subscribe form (rendered inside Stripe Elements) ──────────────────────────
function SubscribeForm({ onSuccess, mode = 'subscribe', label, price }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Same card form either way: starting a membership, or putting a new card on
  // one that already exists (a past-due one is charged straight away).
  const [mutateSubscribe] = useMutation(mode === 'update' ? UPDATE_PAYMENT_METHOD : SUBSCRIBE_BLACK_CARD, {
    onCompleted: () => onSuccess(),
    onError: (err) => {
      setErrorMsg(err.message);
      setProcessing(false);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg(null);

    const cardElement = elements.getElement(CardElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card', card: cardElement,
    });

    if (error) {
      setErrorMsg(error.message);
      setProcessing(false);
      return;
    }
    mutateSubscribe({ variables: { paymentMethodId: paymentMethod.id } });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <CreditCardIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Payment method
        </Typography>
      </Stack>
      <Box sx={{
        p: 2,
        borderRadius: '14px',
        border: '1.5px solid #e2e8f0',
        bgcolor: '#fff',
        transition: 'border-color 0.15s ease',
        '&:focus-within': { borderColor: '#fbbf24', boxShadow: '0 0 0 4px rgba(251,191,36,0.15)' },
        mb: 1.5,
      }}>
        <CardElement
          options={{
            style: {
              base: {
                color: '#0f172a',
                fontSize: '16px',
                '::placeholder': { color: '#94a3b8' },
                fontFamily: FONT_BODY,
              },
              invalid: { color: '#dc2626' },
            },
          }}
        />
      </Box>
      {errorMsg && (
        <Stack direction="row" gap={0.75} sx={{ p: 1.25, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', mb: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: 600 }}>
            {errorMsg}
          </Typography>
        </Stack>
      )}
      <Button
        type="submit"
        fullWidth
        disabled={processing || !stripe}
        sx={{
          py: 1.5,
          borderRadius: '14px',
          fontWeight: 800,
          fontSize: '0.95rem',
          textTransform: 'none',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 60%, #3f3f3f 100%)',
          color: '#fde047',
          letterSpacing: 0.3,
          boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1c1c1c 60%, #2a2a2a 100%)',
          },
          '&.Mui-disabled': { color: 'rgba(253,224,71,0.5)' },
        }}
      >
        {processing
          ? <CircularProgress size={22} sx={{ color: '#fde047' }} />
          : (label || `Subscribe — ${bcMoney(price)} / month`)}
      </Button>
    </Box>
  );
}

// ── QorCommerce subscribe (PayFrame card token → recurring) ───────────────────
function QorSubscribeForm({ profileId, onSuccess, mode = 'subscribe', label, busyLabel, price }) {
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [mutateSubscribe] = useMutation(mode === 'update' ? UPDATE_PAYMENT_METHOD : SUBSCRIBE_BLACK_CARD, {
    onCompleted: () => { setProcessing(false); onSuccess(); },
    onError: (err) => { setErrorMsg(err.message); setProcessing(false); },
  });

  // PayFrame vaults the card and returns a token; the backend sets up the
  // recurring sale and our cron re-charges monthly (processor = QOR).
  const onToken = (token) => {
    setErrorMsg(null);
    setProcessing(true);
    mutateSubscribe({ variables: mode === 'update' ? { qorPaymentToken: token } : { processor: 'QOR', qorPaymentToken: token } });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <CreditCardIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label || `Pay with card — ${bcMoney(price)} / month`}
        </Typography>
      </Stack>
      {errorMsg && (
        <Stack direction="row" gap={0.75} sx={{ p: 1.25, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', mb: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#991b1b', fontWeight: 600 }}>{errorMsg}</Typography>
        </Stack>
      )}
      {processing ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={26} sx={{ color: '#fbbf24' }} />
          <Typography sx={{ fontSize: '0.82rem', color: '#475569', mt: 1 }}>{busyLabel || 'Starting your membership…'}</Typography>
        </Stack>
      ) : (
        <QorPaymentForm
          profileId={profileId}
          onToken={onToken}
          onError={(m) => setErrorMsg(m)}
        />
      )}
    </Box>
  );
}

// ── Main page component ────────────────────────────────────────────────────────
export default function Membership() {
  // eslint-disable-next-line no-unused-vars
  const { STRIPE_PUBLIC_KEY, SERVER_URL } = ConfigurableValues();
  const { profile } = useContext(UserContext);

  const stripePromise = useMemo(
    () => (STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null),
    [STRIPE_PUBLIC_KEY]
  );

  const { data, loading, refetch } = useQuery(MY_BLACK_CARD, {
    fetchPolicy: 'cache-and-network',
    skip: !profile,
  });

  // Open while the member is putting a new card on an existing membership.
  const [updatingCard, setUpdatingCard] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  // Which processor the user picks to (re)subscribe with. QorCommerce is default.
  const [chosenMethod, setPayMethod] = useState('QOR');
  // Admin switches (Configuration → Card payment gateways): offer only the
  // gateways that are on, and fall back when the chosen one is off.
  const configuration = useContext(ConfigurationContext);
  const qorOn = configuration?.qorPaymentsEnabled !== false;
  const stripeOn = configuration?.stripePaymentsEnabled !== false;
  const payMethod = chosenMethod === 'QOR' && !qorOn ? 'STRIPE'
    : chosenMethod === 'STRIPE' && !stripeOn ? 'QOR'
    : chosenMethod;

  const [mutateCancel] = useMutation(CANCEL_BLACK_CARD, {
    onCompleted: () => {
      setCancelLoading(false);
      setCancelDialogOpen(false);
      setSuccessMsg('Your membership has been cancelled. Benefits remain active until your billing period ends.');
      refetch();
    },
    onError: (err) => {
      setCancelLoading(false);
      setCancelDialogOpen(false);
      setSuccessMsg(`Error: ${err.message}`);
    },
  });

  const sub = data?.myBlackCardSubscription;
  const isActive = sub?.status === 'ACTIVE';
  const isCancelled = sub?.status === 'CANCELLED';
  const isPastDue = sub?.status === 'PAST_DUE';

  // Super admin settings: whether new members can join, the price they pay,
  // and the perks. A member keeps the price they joined at.
  const { program, loaded: programLoaded } = useBlackCardProgram();
  const benefits = benefitsFor(program);
  const memberPrice = sub?.monthlyPrice ?? program.monthlyPrice;
  const shownPrice = sub && !isCancelled ? memberPrice : program.monthlyPrice;

  // Which card form an existing member sees. It stays on the gateway the
  // membership runs on while that gateway is on; once the admin turns that one
  // off, the form follows the gateway that is on and the backend moves the
  // membership across. Otherwise a member on a switched-off gateway could
  // never pay.
  const updateGateway = sub?.processor === 'QOR' && qorOn ? 'QOR'
    : sub?.processor === 'STRIPE' && stripeOn ? 'STRIPE'
    : stripeOn && stripePromise ? 'STRIPE'
    : qorOn ? 'QOR'
    : null;

  const handleCancel = () => {
    setCancelLoading(true);
    mutateCancel();
  };

  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Header />

      {/* Hero banner — premium black-gold gradient */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1c1c1c 55%, #3f2c00 130%)',
        pt: { xs: 10, md: 12 },
        pb: { xs: 14, md: 16 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -100, right: -80,
          width: 360, height: 360, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(253,224,71,0.10) 0%, transparent 70%)',
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -160, left: -100,
          width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)',
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2.5}>
            <Box sx={{
              width: { xs: 56, md: 64 }, height: { xs: 56, md: 64 }, borderRadius: '14px',
              background: 'rgba(253,224,71,0.12)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(253,224,71,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <WorkspacePremiumIcon sx={{ color: '#fde047', fontSize: 32 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontSize: '0.72rem', fontWeight: 800, letterSpacing: 3,
                color: '#fde047', textTransform: 'uppercase',
              }}>
                RushMyFood
              </Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: '1.85rem', md: '2.4rem' },
                color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em', mt: 0.25,
              }}>
                Black Card
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', mt: 0.4 }}>
                {dateStr} &middot; Premium monthly membership
              </Typography>
            </Box>
            {/* Status pill */}
            {sub && (
              <Box sx={{
                px: 2.5, py: 1.25, borderRadius: '14px',
                background: 'rgba(253,224,71,0.10)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(253,224,71,0.3)',
                textAlign: 'center', minWidth: 130,
              }}>
                <Typography sx={{ color: 'rgba(253,224,71,0.85)', fontSize: '0.65rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
                  Status
                </Typography>
                <Typography sx={{
                  color: isActive ? '#86efac' : isPastDue ? '#fdba74' : '#fde047',
                  fontSize: '1rem', fontWeight: 800, lineHeight: 1.2, mt: 0.3,
                }}>
                  {isActive ? 'Active' : isPastDue ? 'Past due' : 'Cancelled'}
                </Typography>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Pulled-up content */}
      <Container maxWidth="md" sx={{ mt: { xs: -10, md: -12 }, pb: 6, position: 'relative', zIndex: 2 }}>
        {/* Premium pricing card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #111111 0%, #1f1f1f 55%, #2a2a2a 100%)',
            color: '#fff',
            boxShadow: '0 22px 60px rgba(0,0,0,0.4)',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(253,224,71,0.18)',
            '&::before': {
              content: '""', position: 'absolute', top: -80, right: -40,
              width: 240, height: 240, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(253,224,71,0.12) 0%, transparent 70%)',
            },
            '&::after': {
              content: '""', position: 'absolute', bottom: -100, left: -60,
              width: 240, height: 240, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)',
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            gap={2}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 800, letterSpacing: 2,
                  color: 'rgba(253,224,71,0.85)', textTransform: 'uppercase',
                }}>
                  Membership
                </Typography>
                <Chip
                  label="Premium"
                  size="small"
                  sx={{
                    height: 18, fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.5,
                    bgcolor: 'rgba(253,224,71,0.18)',
                    color: '#fde047',
                    border: '1px solid rgba(253,224,71,0.3)',
                  }}
                />
              </Stack>
              <Stack direction="row" alignItems="baseline" gap={1} sx={{ mt: 0.5 }}>
                <Typography sx={{
                  fontSize: { xs: '2.6rem', md: '3.2rem' }, fontWeight: 900,
                  color: '#fde047', lineHeight: 1, letterSpacing: '-0.03em',
                }}>
                  {bcMoney(shownPrice)}
                </Typography>
                <Typography sx={{
                  fontSize: '1rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600,
                }}>
                  / month
                </Typography>
              </Stack>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', mt: 1 }}>
                Cancel anytime · No commitment
              </Typography>
            </Box>

            {isActive && (
              <Box sx={{
                px: 2, py: 1.25, borderRadius: '12px',
                bgcolor: 'rgba(34,197,94,0.15)',
                border: '1px solid rgba(134,239,172,0.35)',
              }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#86efac' }} />
                  <Typography sx={{ color: '#86efac', fontSize: '0.8rem', fontWeight: 800 }}>
                    Active member
                  </Typography>
                </Stack>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', mt: 0.3 }}>
                  Renews {formatDate(sub.currentPeriodEnd)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Benefits grid */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <Grid item xs={12} sm={6} key={b.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: '16px',
                    border: '1px solid #e5e7eb',
                    bgcolor: '#fff',
                    height: '100%',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 28px rgba(15,23,42,0.08)',
                      borderColor: b.accent,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" gap={1.5}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '12px',
                      bgcolor: `${b.accent}22`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon sx={{ color: b.accent, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
                        {b.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5, mt: 0.3 }}>
                        {b.text}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Status / action section */}
        <Box sx={{ mt: 3 }}>
          {loading && !sub ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#fde047' }} />
            </Box>
          ) : (
            <>
              {successMsg && (
                <Stack
                  direction="row" gap={1}
                  sx={{
                    p: 1.75, mb: 2, borderRadius: '14px',
                    bgcolor: '#dcfce7', border: '1px solid #86efac',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#15803d', mt: 0.15 }} />
                  <Typography sx={{ fontSize: '0.85rem', color: '#15803d', fontWeight: 600 }}>
                    {successMsg}
                  </Typography>
                </Stack>
              )}

              {isPastDue && (
                <Box
                  sx={{
                    p: 1.75, mb: 2, borderRadius: '14px',
                    bgcolor: '#fff7ed', border: '1px solid #fdba74',
                  }}
                >
                  <Stack direction="row" gap={1}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: '#c2410c', mt: 0.15 }} />
                    <Typography sx={{ fontSize: '0.85rem', color: '#9a3412', fontWeight: 600 }}>
                      Payment failed — your membership is past due. Add a card below to turn your benefits back on.
                    </Typography>
                  </Stack>

                  {!updatingCard ? (
                    <Button
                      fullWidth
                      onClick={() => setUpdatingCard(true)}
                      startIcon={<CreditCardIcon />}
                      sx={{
                        mt: 1.5, py: 1.35, borderRadius: '12px',
                        fontWeight: 800, textTransform: 'none', fontSize: '0.9rem',
                        color: '#7c2d12', bgcolor: '#fed7aa',
                        '&:hover': { bgcolor: '#fdba74' },
                      }}
                    >
                      Update payment method
                    </Button>
                  ) : updateGateway === 'QOR' ? (
                    <QorSubscribeForm
                      mode="update"
                      label={`Pay ${bcMoney(memberPrice)} now with a new card`}
                      busyLabel="Charging your card…"
                      profileId={profile?._id ? String(profile._id) : undefined}
                      onSuccess={() => {
                        setUpdatingCard(false);
                        setSuccessMsg('Card updated — your Black Card is active again.');
                        refetch();
                      }}
                    />
                  ) : updateGateway === 'STRIPE' ? (
                    <Elements stripe={stripePromise}>
                      <SubscribeForm
                        mode="update"
                        label={`Pay ${bcMoney(memberPrice)} now`}
                        onSuccess={() => {
                          setUpdatingCard(false);
                          setSuccessMsg('Card updated — your Black Card is active again.');
                          refetch();
                        }}
                      />
                    </Elements>
                  ) : (
                    <Typography sx={{ fontSize: '0.85rem', color: '#9a3412', mt: 1.5 }}>
                      Payment is not configured. Please try again later.
                    </Typography>
                  )}
                </Box>
              )}

              {isCancelled && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25, mb: 2, borderRadius: '16px',
                    bgcolor: '#f8fafc', border: '1px solid #cbd5e1',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <CancelOutlinedIcon sx={{ fontSize: 20, color: '#64748b' }} />
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#334155' }}>
                      Membership cancelled
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: '0.85rem', color: '#475569', mt: 0.5 }}>
                    Benefits remain active until <strong>{formatDate(sub.currentPeriodEnd)}</strong>
                  </Typography>
                </Paper>
              )}

              {/* Sign-ups closed by the super admin. Current members still
                  manage their membership below. */}
              {(!sub || isCancelled) && programLoaded && !program.enabled && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '20px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                    Black Card isn't taking new members right now
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#475569' }}>
                    Please check back later.
                  </Typography>
                </Paper>
              )}

              {(!sub || isCancelled) && programLoaded && program.enabled && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    borderRadius: '20px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                    {isCancelled ? 'Reactivate Black Card' : 'Become a Black Card member'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#475569', mb: 1.5 }}>
                    {isCancelled
                      ? "Start a new membership — you'll be billed monthly until you cancel."
                      : "Choose how to pay. You'll be billed monthly until you cancel."}
                  </Typography>

                  {/* Payment-method chooser: QorCommerce (default) or Stripe */}
                  <Stack direction="row" gap={1} sx={{ mb: 2 }}>
                    {qorOn && (
                    <Chip
                      label="Card (QorCommerce)"
                      onClick={() => setPayMethod('QOR')}
                      variant={payMethod === 'QOR' ? 'filled' : 'outlined'}
                      color={payMethod === 'QOR' ? 'warning' : 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                    )}
                    {stripeOn && stripePromise && (
                      <Chip
                        label="Card (Stripe)"
                        onClick={() => setPayMethod('STRIPE')}
                        variant={payMethod === 'STRIPE' ? 'filled' : 'outlined'}
                        color={payMethod === 'STRIPE' ? 'warning' : 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>

                  {payMethod === 'QOR' ? (
                    <QorSubscribeForm
                      price={program.monthlyPrice}
                      profileId={profile?._id ? String(profile._id) : undefined}
                      onSuccess={() => {
                        setSuccessMsg('Welcome to Black Card! Your membership is now active.');
                        refetch();
                      }}
                    />
                  ) : stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <SubscribeForm
                        price={program.monthlyPrice}
                        onSuccess={() => {
                          setSuccessMsg('Welcome to Black Card! Your membership is now active.');
                          refetch();
                        }}
                      />
                    </Elements>
                  ) : (
                    <Stack direction="row" gap={1} sx={{ mt: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: '10px', border: '1px solid #fca5a5' }}>
                      <WarningAmberIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                      <Typography sx={{ fontSize: '0.85rem', color: '#991b1b', fontWeight: 600 }}>
                        Payment is not configured. Please try again later.
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              )}

              {isActive && !isCancelled && (
                updatingCard ? (
                  updateGateway === 'QOR' ? (
                    <QorSubscribeForm
                      mode="update"
                      label="Save a new card — nothing is charged today"
                      busyLabel="Saving your card…"
                      profileId={profile?._id ? String(profile._id) : undefined}
                      onSuccess={() => {
                        setUpdatingCard(false);
                        setSuccessMsg('Card updated — your next renewal uses it.');
                        refetch();
                      }}
                    />
                  ) : updateGateway === 'STRIPE' ? (
                    <Elements stripe={stripePromise}>
                      <SubscribeForm
                        mode="update"
                        label="Save new card"
                        onSuccess={() => {
                          setUpdatingCard(false);
                          setSuccessMsg('Card updated — your next renewal uses it.');
                          refetch();
                        }}
                      />
                    </Elements>
                  ) : null
                ) : (
                  <Button
                    fullWidth
                    onClick={() => setUpdatingCard(true)}
                    startIcon={<CreditCardIcon />}
                    sx={{
                      mt: 1, py: 1.35, borderRadius: '14px',
                      fontWeight: 700, textTransform: 'none', fontSize: '0.9rem',
                      color: '#334155', bgcolor: 'transparent', border: '1.5px solid #cbd5e1',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                    }}
                  >
                    Update payment method
                  </Button>
                )
              )}

              {isActive && !isCancelled && (
                <Button
                  fullWidth
                  onClick={() => setCancelDialogOpen(true)}
                  startIcon={<CancelOutlinedIcon />}
                  sx={{
                    mt: 1, py: 1.5, borderRadius: '14px',
                    fontWeight: 700, textTransform: 'none', fontSize: '0.9rem',
                    color: '#dc2626',
                    bgcolor: 'transparent',
                    border: '1.5px solid #fca5a5',
                    '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' },
                  }}
                >
                  Cancel membership
                </Button>
              )}
            </>
          )}
        </Box>
      </Container>

      {/* Cancel confirmation dialog */}
      <RmfDialog open={cancelDialogOpen} onClose={() => !cancelLoading && setCancelDialogOpen(false)}>
        <DialogIcon tone="red" sx={{ mb: 2.5 }}>
          <CreditCardOffRoundedIcon />
        </DialogIcon>
        <RmfDialogTitle besideClose={false}>Cancel Black Card?</RmfDialogTitle>
        <DialogText>
          Your benefits (free delivery + bonus points) will remain active until{' '}
          <strong>{formatDate(sub?.currentPeriodEnd)}</strong>. After that date your membership will not renew.
        </DialogText>
        <RmfDialogActions>
          <Box component="button" type="button" onClick={handleCancel} disabled={cancelLoading} aria-busy={cancelLoading} sx={dialogButtonSx.primary}>
            {cancelLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Yes, cancel'}
          </Box>
          <Box component="button" type="button" onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading} sx={dialogButtonSx.secondary}>
            Keep membership
          </Box>
        </RmfDialogActions>
      </RmfDialog>
    </Box>
  );
}
